/**
 * Cache rules shared by the Worker (edge cache) and the web client (IndexedDB cache).
 * Published quests are immutable, so their public detail can be kept long. Nearby lists
 * change when anyone publishes or deletes, so they're kept briefly; a creator's own
 * publish/delete bypasses both caches for a while (see NEARBY_FRESH_PARAM).
 */

/** Edge cache lifetime of a published quest's public detail (JSON and GPX). */
export const QUEST_DETAIL_EDGE_TTL_S = 24 * 3600;
/** Edge cache lifetime of one nearby list. */
export const NEARBY_EDGE_TTL_S = 90;
/** Client cache lifetime of one nearby list. */
export const NEARBY_CLIENT_TTL_MS = 60_000;
/** Client cache lifetime of a published quest's public detail. Revalidated in the background on use. */
export const QUEST_DETAIL_CLIENT_TTL_MS = 7 * 24 * 3600 * 1000;
/** Nearby lat/lng/bbox snap to this many decimals (about 110 m) so close views share entries. */
export const NEARBY_GRID_DECIMALS = 3;
/** Query param that makes the Worker skip its nearby cache read (it still refreshes the entry). */
export const NEARBY_FRESH_PARAM = "fresh";
/** After your own publish/delete, nearby skips both caches this long: the edge TTL plus slack. */
export const OWN_CHANGE_BYPASS_MS = (NEARBY_EDGE_TTL_S + 30) * 1000;

const NEARBY_KEYS = ["minLat", "minLng", "maxLat", "maxLng", "lat", "lng", "radius"] as const;

const scale = 10 ** NEARBY_GRID_DECIMALS;
const fmt = (n: number) => String(Number(n.toFixed(NEARBY_GRID_DECIMALS)));
const snap = (n: number) => fmt(Math.round(n * scale) / scale);
const down = (n: number) => fmt(Math.floor(n * scale) / scale);
const up = (n: number) => fmt(Math.ceil(n * scale) / scale);

/**
 * The nearby query snapped to the grid, in a fixed order, without unknown params (so
 * cache busters like `?y=` or `fresh` never split entries). The box grows outward so
 * everything in the asked view stays in it. Returns null when a value isn't a number;
 * the handler then reports the error itself.
 */
export function normalizeNearbyQuery(query: Record<string, string | undefined>): URLSearchParams | null {
  const out = new URLSearchParams();
  for (const key of NEARBY_KEYS) {
    const raw = query[key];
    if (raw == null || raw === "") continue;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    let value: string;
    if (key === "minLat" || key === "minLng") value = down(n);
    else if (key === "maxLat" || key === "maxLng") value = up(n);
    else if (key === "radius") value = String(Math.round(n));
    else value = snap(n);
    out.set(key, value);
  }
  return out;
}

/** A quest detail the client and edge may keep: published and with no owner/admin-only data. */
export function isPublicQuestDetail(q: {
  status?: string;
  mine?: boolean;
  can_delete?: boolean;
  can_edit?: boolean;
  route_piece_id?: unknown;
  report_count?: unknown;
}): boolean {
  return (
    q.status === "active" &&
    q.mine !== true &&
    q.can_delete !== true &&
    q.can_edit !== true &&
    q.route_piece_id === undefined &&
    q.report_count === undefined
  );
}

/**
 * Strip owner/admin-only fields from a published quest detail so it can live in IndexedDB
 * and match what anonymous viewers receive. Returns null for drafts and non-active quests.
 */
export function questDetailForClientCache(quest: Record<string, unknown>): Record<string, unknown> | null {
  if (quest.status !== "active") return null;
  const out: Record<string, unknown> = { ...quest, mine: false, can_edit: false, can_delete: false };
  delete out.route_piece_id;
  delete out.report_count;
  const checkpoints = out.checkpoints;
  if (Array.isArray(checkpoints)) {
    out.checkpoints = checkpoints.map((cp) => {
      if (!cp || typeof cp !== "object") return cp;
      const spot = { ...(cp as Record<string, unknown>) };
      delete spot.hidden_story;
      return spot;
    });
  }
  return isPublicQuestDetail(out) ? out : null;
}
